import { useMemo, useState } from "react";

const STATUS = {
  ok: { label: "OK", className: "ok", color: "#36c98f", description: "Aucun défaut visible sur les photos fournies." },
  attention: { label: "Vigilance", className: "attention", color: "#f5a623", description: "Élément à contrôler lors de l’inspection physique." },
  probleme: { label: "Problème", className: "problem", color: "#ef5350", description: "Anomalie visible nécessitant un contrôle approfondi." },
  non_visible: { label: "Non visible", className: "hidden", color: "#a7adb7", description: "Zone insuffisamment visible sur les photos fournies." },
};

const ZONES = [
  ["avant", "Avant / capot"],
  ["toit", "Toit et vitrages"],
  ["flanc_gauche", "Flanc gauche"],
  ["flanc_droit", "Flanc droit"],
  ["arriere", "Arrière"],
  ["jantes_pneus", "Jantes et pneus"],
  ["interieur", "Intérieur"],
];

function statusOf(v) { return STATUS[v] ? v : "non_visible"; }
function zoneLabel(k) { return ZONES.find(([z]) => z === k)?.[1] || k; }
function fmt(v) { return v == null || v === "" ? "—" : String(v); }
function fmtKm(v) { const n = Number(v); return Number.isFinite(n) && n > 0 ? `${n.toLocaleString("fr-FR")} km` : "—"; }

function VehicleMap({ zones = {} }) {
  const c = (z) => STATUS[statusOf(zones?.[z])].color;
  return (
    <svg className="viq-inspection-map-svg" viewBox="0 0 360 520" role="img" aria-label="Schéma visuel du véhicule par zones">
      <defs>
        <filter id="viqInspectionShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="5" stdDeviation="7" floodOpacity=".22" />
        </filter>
        <linearGradient id="viqGlass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#14191e"/><stop offset="1" stopColor="#313941"/>
        </linearGradient>
      </defs>
      <g filter="url(#viqInspectionShadow)">
        <rect x="70" y="66" width="220" height="388" rx="96" fill="#15191d" stroke="#555e67" strokeWidth="4" />
        <path d="M100 82 Q180 42 260 82 L280 154 Q180 181 80 154 Z" fill={c("avant")} stroke="#eef2f4" strokeOpacity=".75" strokeWidth="2" />
        <path d="M94 167 Q180 145 266 167 L252 335 Q180 360 108 335 Z" fill={c("toit")} stroke="#eef2f4" strokeOpacity=".62" strokeWidth="2" />
        <path d="M80 158 L108 169 L108 337 L83 357 Q58 275 80 158 Z" fill={c("flanc_gauche")} stroke="#eef2f4" strokeOpacity=".62" strokeWidth="2" />
        <path d="M280 158 L252 169 L252 337 L277 357 Q302 275 280 158 Z" fill={c("flanc_droit")} stroke="#eef2f4" strokeOpacity=".62" strokeWidth="2" />
        <path d="M109 346 Q180 370 251 346 L268 427 Q180 470 92 427 Z" fill={c("arriere")} stroke="#eef2f4" strokeOpacity=".72" strokeWidth="2" />
        <path d="M120 176 Q180 158 240 176 L228 229 Q180 244 132 229 Z" fill="url(#viqGlass)" stroke="#7d8790" strokeWidth="1.5"/>
        <path d="M130 282 Q180 267 230 282 L239 326 Q180 340 121 326 Z" fill="url(#viqGlass)" stroke="#7d8790" strokeWidth="1.5"/>
        <line x1="180" y1="168" x2="180" y2="338" stroke="#0d1114" strokeOpacity=".35" />
        {[92, 270].map((x, xi) => [142, 378].map((y, yi) => (
          <g key={`${xi}-${yi}`}>
            <rect x={x-20} y={y-34} width="40" height="68" rx="16" fill={c("jantes_pneus")} stroke="#dfe5e9" strokeWidth="2" />
            <circle cx={x} cy={y} r="12" fill="#1a1f24" stroke="#cfd5d9" strokeWidth="2" />
          </g>
        )))}
      </g>
      <g className="viq-inspection-map-labels">
        <text x="180" y="34" textAnchor="middle">Avant</text>
        <text x="180" y="501" textAnchor="middle">Arrière</text>
        <text x="32" y="263" textAnchor="middle" transform="rotate(-90 32 263)">Flanc gauche</text>
        <text x="328" y="263" textAnchor="middle" transform="rotate(90 328 263)">Flanc droit</text>
      </g>
    </svg>
  );
}

async function ensureInspectionPdfLib() {
  if (typeof window === "undefined") throw new Error("Génération PDF indisponible.");
  const load = (id, src, ready) => new Promise((resolve, reject) => {
    if (ready()) return resolve();
    const existing = document.getElementById(id);
    if (existing) {
      const timer = window.setInterval(() => { if (ready()) { clearInterval(timer); resolve(); } }, 60);
      window.setTimeout(() => { clearInterval(timer); ready() ? resolve() : reject(new Error("Bibliothèque PDF indisponible.")); }, 8000);
      return;
    }
    const s = document.createElement("script"); s.id = id; s.src = src; s.async = true;
    s.onload = () => ready() ? resolve() : reject(new Error("Bibliothèque PDF incomplète."));
    s.onerror = () => reject(new Error("Chargement PDF impossible."));
    document.head.appendChild(s);
  });
  await load("viq-inspection-jspdf", "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js", () => !!window.jspdf?.jsPDF);
  await load("viq-inspection-autotable", "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js", () => !!window.jspdf?.jsPDF?.API?.autoTable);
  return window.jspdf.jsPDF;
}

function pdfVehicleMap(doc, zones, x, y, w = 58, h = 88) {
  const rgb = {
    ok:[54,201,143], attention:[245,166,35], probleme:[239,83,80], non_visible:[167,173,183], body:[25,30,34], glass:[48,57,65]
  };
  const col = (z) => rgb[statusOf(zones?.[z])];
  const sx = w / 220, sy = h / 388;
  const rx = (v) => x + v * sx, ry = (v) => y + v * sy;
  doc.setDrawColor(90,98,105); doc.setLineWidth(.5); doc.setFillColor(...rgb.body);
  doc.roundedRect(x, y, w, h, 13, 13, "FD");
  doc.setFillColor(...col("avant")); doc.rect(rx(20), ry(10), w - 40*sx, 62*sy, "F");
  doc.setFillColor(...col("toit")); doc.roundedRect(rx(29), ry(82), w - 58*sx, 166*sy, 6, 6, "F");
  doc.setFillColor(...col("flanc_gauche")); doc.rect(rx(4), ry(76), 24*sx, 220*sy, "F");
  doc.setFillColor(...col("flanc_droit")); doc.rect(rx(192), ry(76), 24*sx, 220*sy, "F");
  doc.setFillColor(...col("arriere")); doc.rect(rx(29), ry(258), w - 58*sx, 105*sy, "F");
  doc.setFillColor(...rgb.glass); doc.roundedRect(rx(48), ry(103), w - 96*sx, 50*sy, 3, 3, "F");
  doc.setFillColor(...rgb.glass); doc.roundedRect(rx(48), ry(205), w - 96*sx, 42*sy, 3, 3, "F");
  doc.setFillColor(...col("jantes_pneus"));
  [[0,72],[196,72],[0,270],[196,270]].forEach(([wx,wy]) => doc.roundedRect(rx(wx), ry(wy), 24*sx, 52*sy, 2, 2, "F"));
}

export async function generateVehicleInspectionPdf({ inspection = {}, vehicle = {}, company = null } = {}) {
  const jsPDF = await ensureInspectionPdfLib();
  const doc = new jsPDF({ unit:"mm", format:"a4" });
  const W = 210, M = 11, dark=[24,32,39], text=[26,31,36], sub=[96,102,110], orange=[255,106,26], line=[214,218,222];
  const zones = inspection.zones || {};
  const user = company || (typeof window !== "undefined" ? window.__viq_user : null) || {};
  const companyName = user.siret_name || user.company_name || user.name || "VehicleIQ";
  const statusRows = ZONES.map(([k,l]) => ({ zone:l, statut:STATUS[statusOf(zones[k])].label, observation:STATUS[statusOf(zones[k])].description }));
  const visible = statusRows.filter((_,i)=>statusOf(zones[ZONES[i][0]])!=="non_visible").length;
  const coverage = Math.round((visible / ZONES.length) * 100);
  const now = new Date();
  let y = 13;

  doc.setFont("helvetica","bold"); doc.setFontSize(23); doc.setTextColor(...dark); doc.text("Vehicle", M, y+5);
  const vehW = doc.getTextWidth("Vehicle"); doc.setTextColor(...orange); doc.text("IQ", M+vehW, y+5);
  doc.setFontSize(17); doc.setTextColor(...dark); doc.text("RAPPORT D’INSPECTION VISUELLE", 57, y+4);
  doc.setFont("helvetica","normal"); doc.setFontSize(8.5); doc.setTextColor(...sub); doc.text("Analyse photographique assistée par VIQ.AI®", 57, y+9);
  doc.text(`Édité le ${now.toLocaleDateString("fr-FR")} à ${now.toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"})}`, W-M, y+3, {align:"right"});
  y += 17; doc.setDrawColor(...line); doc.line(M,y,W-M,y); y+=7;

  const vehicleLabel = [vehicle.make || inspection.make || inspection.identified?.make, vehicle.model || inspection.model || inspection.identified?.model, vehicle.year || inspection.year || inspection.identified?.year].filter(Boolean).join(" ") || "Véhicule analysé";
  doc.setFont("helvetica","bold"); doc.setFontSize(13); doc.setTextColor(...text); doc.text(vehicleLabel, M, y);
  doc.setFont("helvetica","normal"); doc.setFontSize(8.5); doc.setTextColor(...sub); y += 5;
  const idBits = [vehicle.plaque || inspection.plate ? `Immatriculation : ${vehicle.plaque || inspection.plate}` : null, vehicle.vin || inspection.vin ? `VIN : ${vehicle.vin || inspection.vin}` : null].filter(Boolean);
  doc.text(idBits.join("  ·  ") || "Identification à compléter", M, y); y += 7;

  doc.autoTable({ startY:y, margin:{left:M,right:M}, theme:"grid",
    head:[["INFORMATIONS GÉNÉRALES","Valeur","INFORMATIONS VÉHICULE","Valeur"]],
    body:[
      ["Entreprise", companyName, "Marque / modèle", vehicleLabel],
      ["Date de l’analyse", now.toLocaleDateString("fr-FR"), "Kilométrage", fmtKm(vehicle.km || inspection.km)],
      ["Photos analysées", String(inspection.photo_count || "—"), "Énergie", fmt(vehicle.fuel || inspection.fuel || inspection.identified?.fuel)],
      ["Couverture photo", `${coverage} %`, "Boîte", fmt(vehicle.gearbox || inspection.gearbox || inspection.identified?.gearbox)],
    ],
    styles:{font:"helvetica",fontSize:7.5,cellPadding:2.2,textColor:text,lineColor:line,lineWidth:.15},
    headStyles:{fillColor:dark,textColor:[255,255,255],fontStyle:"bold"},
    columnStyles:{0:{fontStyle:"bold",cellWidth:31},1:{cellWidth:55},2:{fontStyle:"bold",cellWidth:35},3:{cellWidth:67}}
  });
  y = doc.lastAutoTable.finalY + 6;

  doc.setFont("helvetica","bold"); doc.setFontSize(10.5); doc.setTextColor(...dark); doc.text("INSPECTION VISUELLE PAR ZONE",M,y); y+=3;
  doc.autoTable({ startY:y, margin:{left:M,right:77}, theme:"grid",
    head:[["Zone","Statut","Observations"]],
    body:statusRows.map(r=>[r.zone,r.statut,r.observation]),
    styles:{font:"helvetica",fontSize:7.1,cellPadding:1.8,textColor:text,lineColor:line,lineWidth:.15},
    headStyles:{fillColor:dark,textColor:[255,255,255],fontStyle:"bold"},
    didParseCell:(hook)=>{ if(hook.section==="body"&&hook.column.index===1){ const s=statusRows[hook.row.index]; const meta=Object.values(STATUS).find(x=>x.label===s.statut); if(meta){ const hex=meta.color.replace("#",""); hook.cell.styles.textColor=[parseInt(hex.slice(0,2),16),parseInt(hex.slice(2,4),16),parseInt(hex.slice(4,6),16)]; hook.cell.styles.fontStyle="bold"; } } },
  });
  const tableTop = y + 4;
  pdfVehicleMap(doc,zones,W-68,tableTop,54,95);
  doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(...dark); doc.text("SCHÉMA VISUEL",W-41,tableTop-1,{align:"center"});
  y = Math.max(doc.lastAutoTable.finalY, tableTop+100) + 6;

  if (inspection.synthese || inspection.etat_general) {
    doc.setFillColor(247,248,249); doc.setDrawColor(...line); doc.roundedRect(M,y,W-2*M,18,2,2,"FD");
    doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(...dark); doc.text("SYNTHÈSE",M+4,y+5);
    doc.setFont("helvetica","normal"); doc.setFontSize(8); doc.setTextColor(...text);
    const lines = doc.splitTextToSize(inspection.synthese || inspection.etat_general,W-2*M-8).slice(0,3); doc.text(lines,M+4,y+10); y += 24;
  }

  const vigil = inspection.vigilance_points || inspection.defauts || inspection.defects || [];
  doc.setFont("helvetica","bold"); doc.setFontSize(10.5); doc.setTextColor(...dark); doc.text("POINTS DE VIGILANCE / À VÉRIFIER",M,y); y+=4;
  if (vigil.length) {
    doc.autoTable({ startY:y, margin:{left:M,right:M}, theme:"grid", head:[["#","Observation"]], body:vigil.slice(0,8).map((v,i)=>[String(i+1),typeof v==="string"?v:(v?.titre||v?.label||"Point à contrôler")]), styles:{font:"helvetica",fontSize:7.6,cellPadding:2,lineColor:line,lineWidth:.15}, headStyles:{fillColor:dark,textColor:[255,255,255]}, columnStyles:{0:{cellWidth:10,halign:"center",fontStyle:"bold"}} });
    y=doc.lastAutoTable.finalY+6;
  } else {
    doc.setFont("helvetica","normal"); doc.setFontSize(8); doc.setTextColor(...sub); doc.text("Aucun point de vigilance spécifique détecté sur les photos fournies.",M,y+2); y+=9;
  }

  doc.setFillColor(255,249,244); doc.setDrawColor(255,191,150); doc.roundedRect(M,y,W-2*M,23,2,2,"FD");
  doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(...orange); doc.text("IMPORTANT",M+4,y+5);
  doc.setFont("helvetica","normal"); doc.setFontSize(7.3); doc.setTextColor(...sub);
  const note=doc.splitTextToSize("Ce rapport décrit uniquement les éléments visibles sur les photographies analysées. Il ne remplace ni un contrôle technique, ni une expertise automobile, ni une inspection physique sur pont. Toute zone marquée « Non visible » ou « Vigilance » doit être contrôlée avant décision d’achat ou de revente.",W-2*M-8);
  doc.text(note,M+4,y+10); y+=29;

  doc.setDrawColor(...line); doc.line(M,282,W-M,282);
  doc.setFontSize(6.8); doc.setTextColor(...sub); doc.text("VehicleIQ · VIQ.AI® — Rapport généré automatiquement à partir des données disponibles dans le dossier.",M,287);
  doc.text("vehicleiq.fr",W-M,287,{align:"right"});

  // Page 2 — grille d'inspection inspirée du rapport atelier validé. Les
  // éléments impossibles à confirmer sur photo sont explicitement marqués N/A :
  // le document n'invente jamais une inspection mécanique qui n'a pas eu lieu.
  doc.addPage();
  let y2=13;
  doc.setFont("helvetica","bold"); doc.setFontSize(18); doc.setTextColor(...dark); doc.text("GRILLE D’INSPECTION DU VÉHICULE",M,y2+4);
  doc.setFont("helvetica","normal"); doc.setFontSize(8.5); doc.setTextColor(...sub); doc.text(`${vehicleLabel} · ${vehicle.plaque || inspection.plate || "immatriculation non renseignée"}`,M,y2+10);
  y2+=18;
  const rank={non_visible:0,ok:1,attention:2,probleme:3};
  const worst=(keys)=>{ const vals=keys.map(k=>statusOf(zones[k])); return vals.sort((a,b)=>rank[b]-rank[a])[0] || "non_visible"; };
  const row=(label,status,obs="")=>[label,STATUS[statusOf(status)].label,obs || STATUS[statusOf(status)].description];
  const na=(label,obs="Non inspecté par l’analyse photographique.")=>row(label,"non_visible",obs);
  const sections=[
    ["INSPECTION EXTÉRIEURE",[
      row("État carrosserie et peinture",worst(["avant","toit","flanc_gauche","flanc_droit","arriere"])),
      row("Pare-brise, toit et vitrages",statusOf(zones.toit)),
      na("Éclairage et signalisation"), na("Rétroviseurs"),
      row("Pare-chocs et éléments extérieurs",worst(["avant","arriere"])),
    ]],
    ["INSPECTION INTÉRIEURE",[
      row("État général de l’habitacle",statusOf(zones.interieur)),
      na("Sièges, sellerie et tapis","À contrôler physiquement si non visibles sur les photos."),
      na("Tableau de bord et commandes"), na("Système électrique et électronique"),
      na("Chauffage et climatisation"),
    ]],
    ["PNEUMATIQUES ET JANTES",[
      row("État visuel des pneumatiques",statusOf(zones.jantes_pneus)),
      row("Jantes / déformations visibles",statusOf(zones.jantes_pneus)),
      na("Pression des pneumatiques","Mesure impossible sur photo."),
      na("Roue de secours","Présence non confirmée par l’analyse photo."),
    ]],
    ["COMPARTIMENT MOTEUR / LIAISONS AU SOL",[
      na("Niveaux de fluides"),na("Courroies et durites"),na("Batterie et circuit de charge"),
      na("Suspension et amortisseurs"),na("Direction et rotules"),na("Freinage"),na("Échappement"),na("Transmission"),
    ]],
  ];
  for(const [title,rows] of sections){
    doc.setFont("helvetica","bold"); doc.setFontSize(9.5); doc.setTextColor(...dark); doc.text(title,M,y2); y2+=2;
    doc.autoTable({startY:y2,margin:{left:M,right:M},theme:"grid",head:[["Point contrôlé","Statut","Observations"]],body:rows,styles:{font:"helvetica",fontSize:7,cellPadding:1.7,lineColor:line,lineWidth:.15},headStyles:{fillColor:dark,textColor:[255,255,255]},columnStyles:{0:{cellWidth:58,fontStyle:"bold"},1:{cellWidth:28},2:{cellWidth:102}},didParseCell:(hook)=>{if(hook.section==="body"&&hook.column.index===1){const key=Object.keys(STATUS).find(k=>STATUS[k].label===hook.cell.raw)||"non_visible";const hx=STATUS[key].color.replace("#","");hook.cell.styles.textColor=[parseInt(hx.slice(0,2),16),parseInt(hx.slice(2,4),16),parseInt(hx.slice(4,6),16)];hook.cell.styles.fontStyle="bold";}}});
    y2=doc.lastAutoTable.finalY+6;
    if(y2>252 && title!==sections[sections.length-1][0]){doc.addPage();y2=15;}
  }
  if(y2<242){
    doc.setFont("helvetica","bold");doc.setFontSize(9);doc.setTextColor(...dark);doc.text("CONCLUSION GÉNÉRALE",M,y2);y2+=5;
    doc.setFont("helvetica","normal");doc.setFontSize(8);doc.setTextColor(...text);
    const conclusion=(inspection.synthese||inspection.etat_general||"Inspection visuelle réalisée à partir des photos disponibles.") + " Les éléments marqués N/A doivent être contrôlés physiquement avant décision.";
    doc.text(doc.splitTextToSize(conclusion,W-2*M),M,y2);
  }
  doc.setDrawColor(...line);doc.line(M,282,W-M,282);doc.setFontSize(6.8);doc.setTextColor(...sub);doc.text("VehicleIQ · Rapport d’inspection visuelle — Les contrôles mécaniques non observables sur photo sont volontairement indiqués N/A.",M,287);

  const slug = vehicleLabel.normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[^a-zA-Z0-9]+/g,"-").replace(/^-|-$/g,"").toLowerCase().slice(0,45) || "vehicule";
  doc.save(`VehicleIQ-rapport-inspection-${slug}-${now.toISOString().slice(0,10)}.pdf`);
}

export default function VisualInspectionReport({ inspection = {}, vehicle = {}, photos = [], compact = false }) {
  const [pdfBusy,setPdfBusy]=useState(false);
  const [pdfError,setPdfError]=useState("");
  const zones = inspection?.zones || {};
  const vigil = inspection?.vigilance_points || inspection?.defauts || inspection?.defects || [];
  const counts = useMemo(() => ZONES.reduce((acc,[k]) => { const s=statusOf(zones[k]); acc[s]=(acc[s]||0)+1; return acc; },{}),[zones]);
  const visible = ZONES.length - (counts.non_visible || 0);
  const coverage = Math.round((visible / ZONES.length) * 100);
  const label = [vehicle.make || inspection.make || inspection.identified?.make, vehicle.model || inspection.model || inspection.identified?.model, vehicle.year || inspection.year || inspection.identified?.year].filter(Boolean).join(" ") || "Véhicule analysé";
  const thumbList = Array.isArray(photos) ? photos.filter(Boolean).slice(0,6) : [];

  const downloadPdf = async () => {
    setPdfBusy(true); setPdfError("");
    try { await generateVehicleInspectionPdf({ inspection, vehicle }); }
    catch(e){ setPdfError(e?.message || "Impossible de générer le rapport."); }
    finally { setPdfBusy(false); }
  };

  return <section className={`viq-inspection-report${compact?" compact":""}`}>
    <header className="viq-inspection-head">
      <div className="viq-inspection-title-icon">📷</div>
      <div><h3>Analyse photo IA — Rapport d’inspection visuelle</h3><p>État estimé du véhicule à partir des photos disponibles.</p></div>
      <div className="viq-inspection-head-meta"><span>✓ Analyse terminée</span>{inspection.photo_count ? <small>{inspection.photo_count} photo(s)</small> : null}</div>
    </header>

    <div className="viq-inspection-main-grid">
      <div className="viq-inspection-panel vehicle">
        <span className="viq-inspection-kicker">Véhicule analysé</span>
        {thumbList[0] && <img className="viq-inspection-hero-photo" src={thumbList[0]} alt={label} />}
        <strong className="viq-inspection-vehicle-name">{label}</strong>
        <div className="viq-inspection-vehicle-facts">
          {(vehicle.plaque || inspection.plate) && <span>▣ {vehicle.plaque || inspection.plate}</span>}
          {(vehicle.version || inspection.version || inspection.identified?.version) && <span>◫ {vehicle.version || inspection.version || inspection.identified?.version}</span>}
          {(vehicle.fuel || inspection.fuel || inspection.identified?.fuel) && <span>⛽ {vehicle.fuel || inspection.fuel || inspection.identified?.fuel}</span>}
          {(vehicle.gearbox || inspection.gearbox || inspection.identified?.gearbox) && <span>⚙ {vehicle.gearbox || inspection.gearbox || inspection.identified?.gearbox}</span>}
          {(vehicle.km || inspection.km) && <span>◉ {fmtKm(vehicle.km || inspection.km)}</span>}
        </div>
      </div>

      <div className="viq-inspection-panel map">
        <div className="viq-inspection-panel-head"><span className="viq-inspection-kicker">Vue générale du véhicule</span><small>Cliquez sur une zone pour lire son statut</small></div>
        <VehicleMap zones={zones}/>
        <div className="viq-inspection-legend">{Object.entries(STATUS).map(([k,m])=><span key={k}><i style={{background:m.color}} />{m.label}</span>)}</div>
      </div>

      <div className="viq-inspection-panel details">
        <span className="viq-inspection-kicker">Détails par zone</span>
        <div className="viq-inspection-zone-list">{ZONES.map(([k,l])=>{ const s=statusOf(zones[k]),m=STATUS[s]; return <div key={k} className="viq-inspection-zone-row"><i style={{background:m.color}}/><div><strong>{l}</strong><span>{m.description}</span></div><b className={`status ${m.className}`}>{m.label}</b></div>; })}</div>
      </div>
    </div>

    <div className="viq-inspection-bottom-grid">
      <div className="viq-inspection-panel summary"><span className="viq-inspection-kicker">Résumé</span><div className="viq-inspection-summary-counts"><b className="ok">{counts.ok||0}<small>Zones OK</small></b><b className="attention">{counts.attention||0}<small>Vigilances</small></b><b className="problem">{counts.probleme||0}<small>Problèmes</small></b><b className="hidden">{counts.non_visible||0}<small>Non visibles</small></b></div>{(inspection.synthese||inspection.etat_general)&&<p>{inspection.synthese||inspection.etat_general}</p>}</div>
      <div className="viq-inspection-panel vigilance"><span className="viq-inspection-kicker">Points à vérifier</span>{vigil.length?<ul>{vigil.slice(0,6).map((v,i)=><li key={i}>{typeof v==="string"?v:(v?.titre||v?.label||"Point à contrôler")}</li>)}</ul>:<p>Aucun point de vigilance spécifique détecté.</p>}</div>
      <div className="viq-inspection-panel photos"><span className="viq-inspection-kicker">Photos analysées</span>{thumbList.length?<div className="viq-inspection-thumbs">{thumbList.map((src,i)=><img key={i} src={src} alt={`Photo analysée ${i+1}`} />)}</div>:<p>{inspection.photo_count || 0} photo(s) analysée(s) et conservée(s) dans le dossier.</p>}</div>
      <div className="viq-inspection-panel confidence"><span className="viq-inspection-kicker">Couverture photo</span><div className="viq-inspection-ring" style={{"--pct":`${coverage*3.6}deg`}}><strong>{coverage}%</strong></div><p>{visible}/{ZONES.length} zones exploitables. Les zones non visibles restent à inspecter physiquement.</p></div>
    </div>

    <div className="viq-inspection-footer-actions">
      <button type="button" className="viq-btn primary" onClick={downloadPdf} disabled={pdfBusy}>{pdfBusy?"Génération du rapport…":"📄 Générer le rapport d’inspection PDF"}</button>
      {typeof inspection.score_delta === "number" && inspection.score_delta !== 0 && <span className={`viq-inspection-score ${inspection.score_delta<0?"negative":"positive"}`}>Impact score : {inspection.score_delta>0?"+":""}{inspection.score_delta} pts</span>}
      {pdfError && <span className="viq-inspection-pdf-error">{pdfError}</span>}
    </div>
  </section>;
}
